import { CommonModule } from '@angular/common';
import { Component, Input, computed, signal } from '@angular/core';
import { BadgeComponent } from 'nebula-ui-kit';
import { ExpiringDocument } from '../../../../domain/src/lib/retention/retention.model';

interface ExpiringBucket {
  label: string;
  count: number;
  items: ExpiringDocument[];
}

@Component({
  selector: 'app-retention-calendar',
  standalone: true,
  imports: [CommonModule, BadgeComponent],
  template: `
    <section class="retention-calendar">
      <header class="retention-calendar__header">
        <h3>Calendario de vencimientos</h3>
        <p>Resumen de documentos con vencimiento cercano.</p>
      </header>

      <div class="retention-calendar__grid">
        @for (bucket of buckets(); track bucket.label) {
          <article class="retention-calendar__card">
            <div class="retention-calendar__card-header">
              <strong>{{ bucket.label }}</strong>
              <lib-badge [text]="bucket.count + ' docs'" variant="info" size="small" />
            </div>
            <ul>
              @for (item of bucket.items.slice(0, 5); track item.document_id) {
                <li>
                  <span class="doc">{{ item.document_name || item.document_id }}</span>
                  <span class="days">{{ item.days_until_expiry }} días</span>
                </li>
              }
            </ul>
          </article>
        }
      </div>
    </section>
  `,
  styles: [
    `
      .retention-calendar {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 10px;
        padding: 1rem;
        background: var(--md-sys-color-surface-container-low);
      }
      .retention-calendar__header h3 {
        margin: 0 0 0.25rem;
      }
      .retention-calendar__header p {
        margin: 0 0 1rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      .retention-calendar__grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 0.75rem;
      }
      .retention-calendar__card {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 8px;
        padding: 0.65rem;
        background: var(--md-sys-color-surface);
      }
      .retention-calendar__card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
      }
      ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 0.4rem;
      }
      li {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        font-size: 0.85rem;
      }
      .doc {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .days {
        color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ],
})
export class RetentionCalendarComponent {
  private readonly _documents = signal<ExpiringDocument[]>([]);
  @Input() set documents(value: ExpiringDocument[] | null) {
    this._documents.set(value ?? []);
  }
  @Input() daysWindow = 30;

  readonly buckets = computed<ExpiringBucket[]>(() => {
    const docs = this._documents();
    const thisWeek = docs.filter((d) => d.days_until_expiry <= 7);
    const nextWeek = docs.filter((d) => d.days_until_expiry > 7 && d.days_until_expiry <= 14);
    const later = docs.filter((d) => d.days_until_expiry > 14 && d.days_until_expiry <= this.daysWindow);
    return [
      { label: 'Esta semana', count: thisWeek.length, items: thisWeek },
      { label: 'Próxima semana', count: nextWeek.length, items: nextWeek },
      { label: `Resto (${this.daysWindow} días)`, count: later.length, items: later },
    ];
  });
}
